'use strict';

define('vb/private/stateManagement/fragment',[
  'knockout',
  'vb/private/stateManagement/container',
  'vb/helpers/mixin',
  'vb/private/stateManagement/fragmentHolderBaseMixin',
  'vb/private/stateManagement/eventBehaviorMixin',
  'vb/private/stateManagement/eventHandlingMixin',
  'vb/private/stateManagement/context/fragmentContext',
  'vb/private/stateManagement/fragmentExtension',
  'vb/private/stateManagement/fragmentModuleViewModel',
  'vb/private/log',
  'vbc/private/logConfig',
  'vb/private/monitoring/loadMonitorOptions',
  'vb/private/monitoring/activateMonitorOptions',
  'vb/private/utils',
  'vb/private/constants',
  'vb/private/configuration',
  'vb/private/stateManagement/stateMonitor',
  'vb/private/events/eventMonitorOptions',
  'vb/private/translations/bundleUtils',
], (ko, Container, Mixin, FragmentHolderBaseMixin, EventBehaviorMixin, EventHandlingMixin, FragmentContext,
  FragmentExtension, FragmentModuleViewModel, Log, LogConfig, LoadMonitorOptions, ActivateMonitorOptions, Utils,
  Constants, Configuration, StateMonitor, EventMonitorOptions, BundleUtils) => {
  /**
   * @typedef {import('vb/private/stateManagement/baseModuleViewModel').default} BaseModuleViewModel
   * @typedef {import('vb/private/stateManagement/variable').default} Variable
   * @typedef {import('vb/private/stateManagement/flow').default} Flow
   * @typedef {import('vb/private/events/eventBehavior').default} EventBehavior
   */

  const logger = Log.getLogger('/vb/stateManagement/fragment', [
    // Register a custom logger
    {
      name: 'coralInfo',
      severity: 'info',
      style: 'coral',
    },
    {
      name: 'beforeHandleEvent',
      severity: Constants.Severity.INFO,
      style: LogConfig.FancyStyleByFeature.containerStart,
    },
    {
      name: 'afterHandleEvent',
      severity: Constants.Severity.INFO,
      style: LogConfig.FancyStyleByFeature.containerEnd,
    },
  ]);

  const RESERVED_DEFAULT_SLOT_NAME = '--vb-reserved-slot-default--';

  /**
   * Fragment class: A VB model for representing a fragment instance that is generally associated to a page or a
   * fragment. A page / fragment can contain one or more fragments.
   *
   * One instance of a Fragment class is associated to each occurrence of a <oj-vb-fragment> component in a page or a
   * fragment.
   * A single instance of Fragment is responsible to show one fragment view/viewModel identified by a name and
   * located under /fragments folder.
   *
   * Example author creates html|js|json triad under '/fragments/charlie'. 'charlie' is the name of the fragment.
   *
   * A page / fragment can include the same fragment multiple times. Example 'cartoon' fragment -
   * <oj-vb-fragment id='peanutz' name='cartoon'></oj-vb-fragment>
   * <oj-vb-fragment id='calvin' name='cartoon'></oj-vb-fragment>
   *
   * 2 instances are created, each identified by its 'id'; both fragment instances load the same view/viewModel -
   * charlie. The params passed to each usually will differ.
   * @class Fragment
   * @augments Container
   * @mixes FragmentHolderBaseMixin
   * @mixes EventBehaviorMixin
   * @mixes EventHandlingMixin
   */
  class Fragment extends Mixin(Container).with(FragmentHolderBaseMixin, EventBehaviorMixin, EventHandlingMixin) {
    /**
     * Creates a Fragment instance
     * @param {string} id unique identifier to locate the fragment. Caller must provide a user-friendly value but if not
     * provided a unique key will be created.
     * @param {Container} parent container.
     * @param {string} className name of the class
     * @param {string} name (required) name of the fragment to load. this might come in later. See loadFragment
     * @param {Object|*} params (optional) input parameters provided by caller to this fragment. This will be used to
     * initialize variables in the fragment
     * @param {Object} [fragmentOptions]
     * @param {boolean} [fragmentOptions.autoGeneratedId]
     * @constructor
     */
    constructor(id, parent, className, name, params, templates, fragmentOptions) {
      super(id, parent, (className || 'Fragment'));
      this.log = logger;

      this.fragmentName = name;
      /**
       * tells us which extension defines the fragment. This is set in Package Fragment when the name of the
       * fragment references a fragment defined in another extension
       * @type {string|undefined}
       */
      this.fragmentExtensionId = undefined;
      this.inputParams = params;
      this.templates = templates;
      this._autoGeneratedId = !!(fragmentOptions && fragmentOptions.autoGeneratedId);
      this.viewTemplateByLocator = {};
      this.viewTemplateDataAliasByLocator = {};

      // All fragment resources are located under the "fragments" folder
      // From this point on, the path cannot be modified.
      Object.defineProperties(this, {
        path: {
          get() {
            return `${Constants.DefaultPaths.FRAGMENTS}${this.resourceName}/`;
          },
          enumerable: true,
        },
      });

      /**
       * moduleConfig observable
       * @type {ko.Observable}
       */
      this.moduleConfig = ko.observable(Constants.blankModuleConfig);

      // expressionContext is created early to allow the vb-fragment to be initialized with it. The vb-fragment
      // component initiates loading of the actual fragment artifacts.
      // TODO: container of the fragment might generally creates the context object in its loadMetadata. It needs to be
      //  overridden to stop duplicate creation.
      this.expressionContext = new FragmentContext(this);

      this.fragmentLifecycleState = undefined;
      this.fragmentComponentState = undefined;
      /**
       * tracks fragment dispose state at various stages -
       * Constants.FragmentEagerDisposeState.REQUESTED: indicates it's marked for dispose
       * Constants.FragmentEagerDisposeState.DISPOSING: it's currently being disposed. Example, vbExit phase
       * undefined: either fully disposed or fully active
       */
      this.eagerDisposeState = undefined;

      // Promises
      /** @type {Promise<*>?} */
      this.loadAndStartPromise = null; // created when a fragment is starting to load
      /** @type {Promise<*>?} */
      this.loadFragmentPromise = null; // created when fragment artifacts are loaded
      /** @type {Promise<*>?} */
      this.initializePromise = null; // created when a fragment is initialized and activated; its state primarily
      /** @type {Promise<*>?} */
      this.runPromise = null; // created when a fragment runs vbEnter after module connection
    }

    /**
     * Define the info builtin variable object
     * Adds the 3 properties: id, title and description to the builtin object
     * @returns Promise<Object> a promise that resolve with the builtin info variable value
     */
    async defineInfoBuiltinVariable() {
      return {
        id: this.id,
        title: this.definition.title,
        description: this.definition.description,
      };
    }

    /**
     * returns the FragmentContext constructor used to create the '$fragment' expression context
     * @type {typeof FragmentContext}
     */
    static get ContextType() {
      return FragmentContext;
    }

    static get extensionClass() {
      return FragmentExtension;
    }

    /**
     * A Fragment instance can also be created by another fragment
     * @type {typeof Fragment}
     */
    static get FragmentClass() {
      return Fragment;
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor.
     * @type {string}
     */
    get fullName() {
      return `${this.resourceName}-fragment`;
    }

    /**
     * The resourceName can be different from the name, which is set to the 'id' by container. When it loads a
     * specific resource it must use this property as the actual name of the resource that this fragment represents.
     * Example: fragment id is unique foo1/foo2 etc. Each instance of fragment loads the same resource
     * 'foo-fragment.*', So resourceName is 'foo'. This rule may not apply to other containers.
     * @type {string}
     */
    get resourceName() {
      return this.fragmentName;
    }

    /**
     * Return the first flow up in the parent hierarchy.
     * For fragment it's the parent flow of the container with the fragment
     *
     * @return {Flow} the first flow in the parent hierarchy
     */
    getParentFlow() {
      return this.parent.getParentFlow();
    }

    /**
     * returns false for event that is not declared
     * @param eventModel
     * @returns {boolean}
     */
    canFireEvent(eventModel) {
      if (eventModel && !eventModel.isDeclared) {
        return false;
      }
      return super.canFireEvent(eventModel);
    }

    /**
     * Creates a moduleConfig object similar to what oj-module expects.
     * @returns {{view: (Promise<string|string[]>), viewModel: Promise<Object>, params: (string)}}
     */
    createModuleConfig() {
      return {
        // initialize the variables before returning the viewModel
        viewModel: this.getViewModel(),
        // params is only used to know which fragment is represented
        params: this.fullPath,
        view: this.getView(),
      };
    }

    /**
     * Returns true if access to fragment is allowed from current context
     * @param descriptor fragment descriptor. subclasses can override to do specific checks
     * @returns {boolean}
     */
    // eslint-disable-next-line no-unused-vars,class-methods-use-this
    checkFragmentAccess(descriptor) {
      return true;
    }

    /**
     * Called after the fragment CCA view is first inserted into the DOM and then each time the composite is
     * reconnected to the DOM after being disconnected.
     */
    componentConnected() {
      if (this.isAutoGeneratedIdDisposing()) {
        // Disposal is terminal for generated-id fragments; do not let a late reconnect revive stale state.
        return;
      }
      // Reconnect cancels any pending deferred disposal scheduled by a prior disconnect.
      this.cancelPendingAutoGeneratedDispose();
      this.fragmentComponentState = Constants.FragmentComponentState.COMPONENT_CONNECTED;
    }

    /**
     * Cancels deferred generated-id disposal scheduled by a prior disconnect.
     */
    cancelPendingAutoGeneratedDispose() {
      this.pendingAutoGeneratedDisposeRequest = undefined;
    }

    /**
     * Returns true if this fragment id was generated by the fragment component.
     * @returns {boolean}
     */
    isAutoGeneratedId() {
      return this._autoGeneratedId;
    }

    /**
     * Returns true if this generated-id fragment is already being disposed.
     * @returns {boolean}
     */
    isAutoGeneratedIdDisposing() {
      return this.isAutoGeneratedId()
        && this.eagerDisposeState === Constants.FragmentEagerDisposeState.DISPOSING;
    }

    /**
     * Returns true when an auto-generated-id fragment has been removed from DOM and is still eligible for disposal.
     * @returns {boolean}
     */
    isRemovedAutoGeneratedFragmentReadyForDispose() {
      return this.isAutoGeneratedId()
        && this.parent.lifecycleState !== Constants.ContainerState.DISPOSED
        && this.parent.lifecycleState !== Constants.ContainerState.EXITED
        && this.fragmentComponentState === Constants.FragmentComponentState.COMPONENT_REMOVED
        && this.lifecycleState !== Constants.ContainerState.DISPOSED
        && this.lifecycleState !== Constants.ContainerState.EXITED
        && this.eagerDisposeState !== Constants.FragmentEagerDisposeState.DISPOSING;
    }

    /**
     * Fragment CCA disconnected callback invoked when this composite component is disconnected from the DOM. This
     * method might be called multiple times.
     * @param fragmentNodes the children of oj-vb-fragment are provided here. See fragmentViewModel#disconnected
     * https://jet.us.oracle.com/11.0.0/jsdocs/Composite.html#ViewModel
     * @param fragmentNode the oj-vb-fragment element when available
     */
    componentDisconnected(fragmentNodes, fragmentNode) {
      // stop observing ojModule node.
      this.fragmentComponentState = Constants.FragmentComponentState.COMPONENT_DISCONNECTED;
      let disconnectedFragmentNode = fragmentNode;
      if (fragmentNodes && fragmentNodes.length > 0) {
        const ojModuleNode = fragmentNodes[0];
        Utils.vbModuleObserver.unobserveModuleNode(ojModuleNode);

        if (ojModuleNode && ojModuleNode.localName === 'oj-module') {
          // ojModule parentNode is oj-vb-fragment, which should be present even as the subtree is being detached.
          disconnectedFragmentNode = ojModuleNode.parentNode;
        }
      }

      // Checking parentNode.parentNode only catches direct removal; isConnected also handles ancestor removal.
      // This lets us distinguish actual DOM removal from temporary disconnect/reconnect callbacks, even if the
      // oj-module child was never created before the fragment component was removed.
      if (disconnectedFragmentNode && !disconnectedFragmentNode.isConnected) {
        this.log.info('The fragment component has been removed from DOM', this.id);
        this.fragmentComponentState = Constants.FragmentComponentState.COMPONENT_REMOVED;
      }

      if (this.isRemovedAutoGeneratedFragmentReadyForDispose() && !this.pendingAutoGeneratedDisposeRequest) {
        // Use a unique object marker so reconnect or a newer disconnect can cancel this deferred disposal. If a
        // disposal request is already pending, keep its original action-chain waits.
        const disposeRequest = {};
        this.pendingAutoGeneratedDisposeRequest = disposeRequest;
        const pendingStartupPromise = this.runPromise || this.initializePromise
          || this.loadAndStartPromise || this.loadFragmentPromise;
        // Defer disposal until known work around the removed fragment has settled, then re-check eligibility.
        // The parent wait covers close/save chains that remove this fragment; the subtree wait covers chains owned by
        // child layouts/fragments inside the fragment being removed.
        Promise.resolve()
          .then(() => (pendingStartupPromise ? pendingStartupPromise.catch(() => {}) : undefined))
          .then(() => this.waitForPendingActionChains(() => [
            ...this.parent.getOwnPendingActionChainPromises(),
            ...this.getPendingActionChainPromisesInSubtree(),
          ]))
          .then(() => {
            if (this.pendingAutoGeneratedDisposeRequest === disposeRequest
              && this.isRemovedAutoGeneratedFragmentReadyForDispose()) {
              this.log.info('Disposing auto-generated-id fragment on disconnect', this.id, this.fullPath);
              // A generated id will not match the next logical instance, so settled removal means this state is stale.
              this.fragmentLifecycleState = Constants.FragmentState.MODULE_DISCONNECTED;
              // This fragment was removed and its generated id cannot be reused, so cleanup should continue even if
              // vbExit fails.
              return this.disposeFragmentStateAfterExit({ disposeOnExitFailure: true });
            }
            return Promise.resolve();
          })
          .catch((error) => {
            this.log.error('Unable to dispose auto-generated-id fragment on disconnect', this.id, this.fullPath, error);
          });
      }

      // Additionally, if component is being disconnected initiate dispose of fragment state if needed. It's funky
      // to be calling async method here but hopefully this is ok, and only truly executed when fragment deleteState
      // has been explicitly requested
      return this.disposeFragmentState();
    }

    /**
     * Disposes this fragment's state, including its children. Also, explicitly calls vbEXIT event since this
     * dispose occurs outside of page exit().
     * @returns {Promise<void>}
     */
    disposeFragmentState() {
      if (this.isReadyForDisposeFragmentState()) {
        const logMsg = this.fragmentComponentState === Constants.FragmentComponentState.COMPONENT_DISCONNECTED
          ? 'Fragment component has been disconnected from the viewmodel but not removed from the DOM.' : '';
        this.log.info(logMsg, 'Deleting state for fragment', this.id);

        return this.disposeFragmentStateWhenReady();
      }

      return Promise.resolve();
    }

    /**
     * Returns whether this fragment has been explicitly requested for disposal and both fragment lifecycle callbacks
     * have reached a disconnected or removed state, which is required before fragment state can be disposed safely.
     * This check is repeated after deferred waits because reconnect, parent teardown, or another disposal path can
     * change lifecycle state before cleanup runs.
     * @returns {boolean} true when fragment state disposal can proceed; otherwise false.
     */
    isReadyForDisposeFragmentState() {
      return this.eagerDisposeState === Constants.FragmentEagerDisposeState.REQUESTED
        && this.fragmentLifecycleState === Constants.FragmentState.MODULE_DISCONNECTED
        && (this.fragmentComponentState === Constants.FragmentComponentState.COMPONENT_DISCONNECTED
          || this.fragmentComponentState === Constants.FragmentComponentState.COMPONENT_REMOVED);
    }

    /**
     * Waits for any in-flight generated-id fragment startup to settle, then rechecks disposal eligibility before
     * disposing fragment state. Explicit-id fragments keep the existing immediate disposal behavior.
     * @returns {Promise<void>} resolves when disposal completes or is skipped because the fragment is no longer ready.
     */
    disposeFragmentStateWhenReady() {
      const pendingStartupPromise = this.isAutoGeneratedId()
        && (this.runPromise || this.initializePromise || this.loadAndStartPromise || this.loadFragmentPromise);
      const readyToDispose = pendingStartupPromise ? pendingStartupPromise.catch(() => {}) : Promise.resolve();

      return readyToDispose.then(() => {
        if (this.isReadyForDisposeFragmentState()) {
          return this.disposeFragmentStateAfterExit();
        }
        return Promise.resolve();
      });
    }

    /**
     * Runs vbExit before disposing fragment state and clearing the eager-dispose marker.
     * @param {Object} [options]
     * @param {boolean} [options.disposeOnExitFailure=false]
     * @returns {Promise<void>}
     */
    disposeFragmentStateAfterExit(options = {}) {
      const { disposeOnExitFailure = false } = options;
      this.eagerDisposeState = Constants.FragmentEagerDisposeState.DISPOSING;

      // Keep the catch before the dispose step: generated-id cleanup may continue after a vbExit failure,
      // but errors from dispose() itself should still reject.
      return this.invokeEvent(Constants.EXIT_EVENT)
        .catch((error) => {
          this.log.error('Unable to run vbExit before disposing fragment state', this.id, this.fullPath, error);
          if (!disposeOnExitFailure) {
            // Disposal did not complete, so restore REQUESTED instead of making the removed fragment look reusable
            // or leaving it permanently stuck in DISPOSING.
            this.eagerDisposeState = Constants.FragmentEagerDisposeState.REQUESTED;
            throw error;
          }
        })
        .then(() => {
          this.lifecycleState = Constants.ContainerState.EXITED;
          this.dispose();
          // set to undefined only after dispose is fully done.
          this.eagerDisposeState = undefined;
          this.log.info('The fragment state has been disposed', this.id);
        });
    }

    /**
     * Handles the vbExit event as a special behavior where the fragment walks all its nested fragments to fire exit
     * event.
     * @param {string} eventName
     * @param {Object} [eventPayload]
     * @param {EventBehavior} [eventBehavior]
     * @param {*} [previousResult]
     * @returns {Promise<unknown>|Promise}
     */
    invokeEvent(eventName, eventPayload, eventBehavior, previousResult) {
      return Promise.resolve().then(() => {
        const promises = [];

        if (eventName === Constants.EXIT_EVENT) {
          this.getChildFragments().forEach((frag) => {
            if (frag.lifecycleState !== Constants.ContainerState.DISPOSED) {
              promises.push(frag.invokeEvent(eventName));
            }
          });

          // wait for child fragment vbExit promises to be fulfilled before invoking same event for current fragment
          return Promise.all(promises).then(() => super.invokeEvent(eventName));
        }

        return super.invokeEvent(eventName, eventPayload, eventBehavior, previousResult);
      });
    }

    /**
     * The name of the runtime environment function to be used to load the descriptor
     *
     * @type {String} the descriptor loader function name
     */
    static get descriptorLoaderName() {
      return 'getFragmentDescriptor';
    }

    dispose() {
      // Mutates ojModule in order to release inner ko bindings
      // this.moduleConfig(Constants.blankModuleConfig);

      this.parent.deleteFragment(this.id, this);

      delete this.definition;

      this.initializePromise = null;
      this.runPromise = null;

      this.viewModelPromise = null;
      this.loadAndStartPromise = null;

      // should dispose the scope from the store.
      super.dispose();
    }

    /**
     * Enter a fragment, i.e, fragment needs to be loaded, initialized and activated. This is almost always happens
     * under the following scenarios -
     * (1) when a vb-fragment CCA starts to render
     * (2) when a vb-fragment CCA disconnects and reconnects
     * (3) (unsupported) when a vb-fragment 'name' property changes, requiring a new fragment to be loaded
     *
     * @returns {Promise<void>} resolved immediately as the intent is to create a moduleConfig object whose viewModel
     * and view properties are Promises that are pending, until they are fully resolved. The moduleConfig is set on
     * the observable this.moduleConfig, which is returned from getModuleConfig() method.
     */
    enter() {
      // we kick off the loading, init and activation steps for the fragment
      this.getInitializePromise();

      const newModuleConfig = this.createModuleConfig();
      this.moduleConfig(newModuleConfig);
      return Promise.resolve(); // required to match base signature
    }

    /**
     * The name of the runtime environment function to be used to load the module functions
     *
     * @type {String} the module loader function name
     */
    static get functionsLoaderName() {
      return 'getFragmentFunctions';
    }

    /**
     * The name of the runtime environment function to be used to load the fragment rules descriptor
     *
     * @type {String} the rules descriptor loader function name
     */
    static get rulesDescriptorLoaderName() {
      return 'getFragmentRulesDescriptor';
    }

    /**
     * The callback used to dispatch event to fragment CCA
     * @returns {*}
     */
    getEventDispatcher() {
      return this.availableContexts[Constants.ContextNameInternal.DISPATCH_EVENT];
    }

    /**
     * Returns a Promise that resolves when fragment has been initialized and activated. If the fragment enter has
     * been vetoed by beforeEnter activation of variables is skipped and this method returns undefined
     * @returns {*|Promise<this|undefined>} resolves when fragment has been fully initialized and activated. returns
     * undefined if
     * the viewModel cannot be initialized; else returns this
     */
    getInitializePromise() {
      // we are here because we are asked to load a new fragment when we are in the middle of loading a different
      // fragment.
      this.initializePromise = this.initializePromise || this.loadAndStart()
        .then((result) => {
          if (!result) {
            // loadAndStart returns undefined if the fragment cannot be entered!
            // TODO: what should we do here when beforeEnter is canceled?
            this.log.info('vbBeforeEnter event listener has canceled entering the fragment', this.resourceName,
              'with id', this.id);
            return undefined;
          }
          return this.initializeScopeAndContextVariables().then(() => this);
        });

      return this.initializePromise;
    }

    /**
     * Returns the value for the variable using input params from parent container.
     * @param {String} variableName
     * @param {Object} variableDef
     * @returns {*}
     */
    getInputParameterFromCallerValue(variableName, variableDef) {
      // first load the value from runtimeEnvironment which takes precedence over everything else
      // NOTE: This will be used by the DT to pass parameters different fragments
      let fromCallerValue = super.getInputParameterFromCallerValue(variableName);

      if (fromCallerValue === undefined) {
        const fip = this.inputParams;
        fromCallerValue = fip && fip[variableName] && fip[variableName].value;
        if (variableDef.required && fromCallerValue === undefined) {
          if (Utils.isInstanceType(variableDef.type)) {
            this.log.error('A required value was not provided for fragment variable', variableName, '! A value must',
              ' be provided for variables that require a reference.');
          } else {
            this.log.error('A required value was not provided for fragment variable', variableName, '. A value will be',
              'attempted to be initialized from other sources such as History, persistence store, configuration.');
          }
        }
      }
      return fromCallerValue;
    }

    /**
     * Create and add a variable that references another variable.
     *
     * @param {string} variableName - The name of the reference variable.
     * @param {string} namespace - The namespace for the reference variable.
     * @param {*} initialValue - The initial value of the variable being referenced.
     * @returns {boolean} True if a reference variable was added, false otherwise.
     */
    createReferenceVariable(variableName, namespace, initialValue) {
      let refVariablesAdded = false;

      // if the initialValue is an instance of an extended class, e.g., ServiceDataProvider,
      // we need to look up the actual referenced variable and the instance properties variable and
      // create reference variables for them in order to support pass-by-reference.
      if (Utils.isExtendedType(initialValue)) {
        const context = initialValue && initialValue.container && initialValue.container.expressionContext;
        refVariablesAdded = this.scope.addReferenceVariable(initialValue, context, variableName, namespace);
      }

      return refVariablesAdded;
    }

    /**
     * Used by event processing. For Fragment containers, we start (and end) with the Fragment itself,
     * unless it is a "dynamicComponent" event, which uses a completely different propagation implementation.
     * (For 'dynamicComponent' behavior, FireCustomEventAction delegates to the JET component.)
     * @see FireCustomEventAction
     *
     * @overrides Container.getLeafContainer
     * @returns {Fragment}
     */
    getLeafContainer() {
      return this;
    }

    /**
     * locates the slot config for the slot name. The slot content is typically defined along with the
     * fragment usage.
     * @param {String} name
     * @param {NodeList} defaultTemplates
     * @returns {Promise<{view: *|string, data: Promise<BaseModuleViewModel>}>}
     */
    getSlotConfig(name, defaultTemplates) {
      return Promise.resolve().then(() => {
        const slotViewName = this.getSlotViewLocator(name, defaultTemplates);
        const isDefaultTemplate = slotViewName === `${RESERVED_DEFAULT_SLOT_NAME}:${name}`;
        return {
          view: this.viewTemplateByLocator[slotViewName],
          data: this.getSlotViewModel(isDefaultTemplate),
          alias: this.viewTemplateDataAliasByLocator[slotViewName],
        };
      });
    }

    /**
     * returns the slot view name to use to locate the slot (view) content. The very first time this method is
     * called it locates the slot view content for the provided name and caches it.
     * @param {String} name
     * @param {*} defaultTemplates
     * @returns {*|string}
     */
    getSlotViewLocator(name, defaultTemplates) {
      const allowsSlots = this.getCapability(Constants.FragmentCapability.ALLOWS_SLOTS);
      if (allowsSlots && !this.viewTemplateByLocator[name]) {
        if (this.templates && this.templates.length > 0) {
          const tarr = [...this.templates];
          const template = tarr.find((t) => (t.getAttribute('slot') === name));
          if (template) {
            const content = (template && template.innerHTML) || ''; // template.content returns the actual DOM
            if (!content) {
              // it is not that bad for fragment author to not provide a slot content, since we fall back on the default
              this.log.info('Unable to locate the fragment slot content for the slot:', name,
                '. Please check your fragment configuration!');
            }
            this.viewTemplateByLocator[name] = content;
            this.viewTemplateDataAliasByLocator[name] = template.getAttribute('data-oj-as') || '';
          }
        }
      }

      // either a template was provided by user, or if not use defaultTemplate or ''. Default template is also used
      // if the 'allow_slots' capability is disabled. Example if fragment is used inside layout.
      return this.viewTemplateByLocator[name] ? name : (this.getDefaultSlotViewLocator(name, defaultTemplates) || '');
    }

    /**
     * returns default slot view name. The very first time this method is called it locates the default slot view
     * content for the provided name and caches the same.
     * @param name
     * @param defaultTemplates
     * @returns {string}
     */
    getDefaultSlotViewLocator(name, defaultTemplates) {
      const defaultSlotContentLocator = `${RESERVED_DEFAULT_SLOT_NAME}:${name}`;
      if (!this.viewTemplateByLocator[defaultSlotContentLocator]) {
        const t = defaultTemplates && defaultTemplates[0];
        let defaultContent = (t && t.innerHTML);
        if (!defaultContent) {
          // if a default content was not provided we warn because CCA architecture recommends that a default
          // content be provided for every slot.
          defaultContent = '';
          this.log.warn('Unable to locate a default slot content for the slot:', name,
            '. It is recommended to have a default view content. Please check your fragment configuration!');
        }
        this.viewTemplateByLocator[defaultSlotContentLocator] = defaultContent;
        this.viewTemplateDataAliasByLocator[defaultSlotContentLocator] = t && t.getAttribute('data-oj-as');
      }
      return defaultSlotContentLocator;
    }

    /**
     * Unlike the fragment content which are executed in the scope of the fragment's view model, the fragment slot
     * view model is executed in the context of the parent container that defined and provided the content for the
     * slot. Example, bucket-list fragment includes a CCA that has a 'header' slot. The content for the header slot
     * is provided by the page, which configures some html with access to the page's context. The text and the
     * button access $page and $flow and this content in then injected into the fragment-slot that is itself running
     * in the context of the fragment but the injected content in the parent's. Here $fragment scope is not available to
     * the content.
     *
     * <oj-vb-fragment name="bucket-list" bridge="[[ vbBridge ]]">
     *  <template slot="someName">
     *    <oj-bind-text value="{{ $page.variables.title }}"></oj-bind-text>
     *    <oj-button on-oj-action="{{ $flow.listeners.notify }}"></oj-button>
     *  </template>
     * </oj-vb-fragment>
     * @param {boolean} useFragmentContext true if the fragment view model needs to be returned
     * @returns {*}
     */
    getSlotViewModel(useFragmentContext = false) {
      let errMsg;
      if (useFragmentContext) {
        // when default template is used, the VB view model needs to be the fragment
        return this.getViewModel();
      }

      if (!this.parent) {
        errMsg = `unable to locate the parent container for the current fragment ${this.name} to create a view`
          + ' model for the fragment slot.';
        throw new Error(errMsg);
      }
      if (this.getCapability(Constants.FragmentCapability.ALLOWS_SLOTS) === true) {
        return this.parent.getViewModel();
      }
      errMsg = `use of slots in the container for the current fragment' ${this.name} is not supported.`
        + ' Please check your configuration.';
      throw new Error(errMsg);
    }

    /**
     * Called when a fragment is being loaded by the CCA, either for the first time or when the name of the
     * fragment to load changes.
     * @returns {ko.Observable}
     */
    getModuleConfig() {
      // when this method is called we have to run
      // - beforeExit event listeners for the currently loaded / active fragment
      // - if this passes then
      //    - deactivate old fragment whatever that means
      //    - start loading new fragment (loadFragment)
      //      - invoke beforeEnter of the new fragment
      //      - if beforeEnter cancels loading then rollback and go to old fragment
      //      - otherwise activate fragment; return a moduleConfig for the same
      this.enter(); // fills the below ko observable with an object whose properties are pending
      return this.moduleConfig;
    }

    /**
     * Returns a scope resolver map where keys are scope names ("global" / "fragment")
     * and value the matching objects. This is used to build the scopeResolver object.
     *
     * @returns {Object} an object which properties are scope
     */
    getScopeResolverMap() {
      // 'global' and 'application' are the same for base (old style apps). While 'global' refers to unified app in
      // extensions, 'application' refers to the appUI which is not available for fragments in extensions.
      return {
        [Constants.FRAGMENT_PREFIX]: this,
        [Constants.GLOBAL_PREFIX]: this.application,
        [Constants.APPLICATION_PREFIX]: this.application,
      };
    }

    /**
     * creates the view
     * @see Fragment.createModuleConfig
     * @returns {Promise<String>}
     */
    getView() {
      return this.loadFragment().then((results) => results[1]);
    }

    /**
     * Returns the viewModel object for the fragment moduleConfig. The model contains all the accessible $ properties
     * of the fragment container. All properties are initialized and ready to be evaluated in expressions.
     * Extensions have also been applied to the fragment.
     * The model is bound to the fragment view by the VB fragment component (oj-vb-fragment).
     *
     * {
     *   $variables: {}
     *   $constants: {}
     *   $chains: {}
     *   $functions: {}
     *   $listeners: {}
     * }
     *
     * @returns {Promise<FragmentModuleViewModel>} a promise that resolve with the module view model instance.
     */
    getViewModel() {
      if (!this.viewModelPromise) {
        // Initialize/Activate the variables before returning the viewModel. initializePromise can return null
        // viewModel if beforeEnter failed or there was some other error.
        this.viewModelPromise = this.getInitializePromise()
          .then(() => BundleUtils.whenBundlesReady())
          .then(() => new FragmentModuleViewModel(this));
      }
      return this.viewModelPromise;
    }

    /**
     * when a fragment variable is marked as writable add a valueChanged listener so that the CCA can be notified of
     * the value change. This is explicitly wired as fragment params are not direct properties on the vb-fragment CCA
     * value changes for whatever reason is not getting written back automatically.
     * @param {Variable} newVariable
     * @param {Object} onValueChangedDef the valueChanged def on the variable
     * @param availableContexts
     */
    addValueChangedListeners(newVariable, onValueChangedDef, availableContexts) {
      const def = newVariable.descriptor;
      // only variables marked writeback:true and input:fromCaller are written back
      if (def && def[Constants.VariableProperties.WRITEBACK]
        && def[Constants.VariableProperties.INPUT] === Constants.VariablePropertyInput.FROM_CALLER) {
        this.handleValueChangedListenerForWritable(newVariable, onValueChangedDef, availableContexts);
      }
      super.addValueChangedListeners(newVariable, onValueChangedDef, availableContexts);
    }

    getVariableDescriptor(variableDef, descriptor) {
      const superDesc = super.getVariableDescriptor(variableDef, descriptor);
      return Object.assign({ writeback: variableDef[Constants.VariableProperties.WRITEBACK] },
        superDesc, descriptor);
    }

    /**
     * Internal valueChanged listener that writes back value on fragment CCA's mapped variable
     * @param newVariable
     * @param {Object} onValueChangedDef mostly unused, since this is relevant for explicitly configured VCLs.
     * @param availableContexts
     */
    handleValueChangedListenerForWritable(newVariable, onValueChangedDef, availableContexts) {
      if (!newVariable.onValueChanged) {
        this.log.warn(newVariable.name, 'does not have a live expression so its onValueChanged listener is ignored');
      }
      const varChangeTracker = {
        eventSource: newVariable.onValueChanged,
        eventListener: (e) => {
          const eventPayload = e || {};
          const context = availableContexts.clone();
          context[Constants.ContextName.EVENT] = e; // capture event payload on the scope
          const eventName = e.type; // vb event type and name is the same
          logger.beforeHandleEvent(this.className, this.id,
            'handling variable event', eventName, 'with payload:', e);
          const mo = new EventMonitorOptions(EventMonitorOptions.SPAN_NAMES.EVENT_VARIABLE, eventName, e, this);
          return this.log.monitor(mo, (eventTime) => {
            // notify the outer context of the value change in current fragment (scope). The container is at most
            // privy to some methods on the fragment bridge, like valueChanged
            const parentBridge = this.parent.getFragmentBridge();
            try {
              parentBridge.valueChanged(Object.assign({}, eventPayload, { _vb_fragmentId: this.id }));
              logger.afterHandleEvent(this.className, this.id,
                'handled variable event', eventName, 'successfully', eventTime());
            } catch (error) {
              logger.afterHandleEvent('Failed to handle variable event', eventName, eventTime(error));
              this.log.error(error);
            }
          });
        },
      };

      // when require-ut is enabled and the param has immediate dispatch enabled, deliver the changes to writeback
      // variables using a microtask
      const fip = this.inputParams;
      const fipDef = fip && fip[newVariable.name];
      const isImmediateDispatch = fipDef && fipDef.writebackMode === 'immediate' && !!Configuration.isRequireUt;

      newVariable.addValueChangedListener(varChangeTracker.eventListener, this, isImmediateDispatch);
      this.variablesListeners.push(varChangeTracker);
    }

    /**
     * Initializes the variables defined in the fragment model into the fragment scope, then sets up the context for the
     * same.
     *
     * @returns {Promise} A promise that resolves when complete
     */
    initializeScopeAndContextVariables() {
      const uniqueId = `[${this.id}: ${this.resourceName}]`;

      // Create the fragment variables using the fragment metadata
      return this.loadFragment()
        .then(() => {
          const mo = new ActivateMonitorOptions(
            this.activateSpanName, `fragment activate ${uniqueId}`, this,
          );

          return this.log.monitor(mo, (fragmentLoadTimer) => this.initAllVariableNamespace()
            .then(() => {
              this.log.coralInfo('Fragment', uniqueId, 'of parent', this.parent.id, 'ACTIVATED.', fragmentLoadTimer());
            })
            .catch((error) => {
              const message = (error && error.message) || 'Unknown error';

              this.log.coralInfo('Fragment', uniqueId, 'of parent', this.parent.id, 'failed to activate because of'
                + ' error: ', message, fragmentLoadTimer(error));
              this.dispose();

              throw error;
            }));
        });
    }

    /**
     * Mostly used to track loading time. It calls loadFragment artifacts. Called only when the name of the
     * fragment to load is known. Typically, this happens when the page renders and encounters a <oj-vb-fragment>
     * with a name attribute set. Or when the name is bound to an expression and the name of the fragment to load
     * also changes.
     *
     * @returns {Promise} a promise that resolves to a fragment instance or undefined if error. At this stage the
     * fragment has been 'loaded'.
     */
    loadAndStart() {
      this.loadAndStartPromise = this.loadAndStartPromise || Promise.resolve().then(() => {
        const uniqueId = `[${this.id}: ${this.resourceName}]`;

        // Start the fragment load timer
        const mo = new LoadMonitorOptions(this.loadSpanName, `fragment load ${uniqueId}`, this)
          .addTags(() => ({
            name: this.fragmentName,
            extensionId: this.fragmentExtensionId,
          }));

        return this.log.monitor(mo, (fragmentLoadTimer) => this.loadFragment()
          .then((results) => {
            // do a prelim check whether access is allowed
            if (!this.checkFragmentAccess(results[0])) {
              throw new Error(`Fragment ${this.resourceName} does not allow other extensions to reference it. `
                + 'The fragment configuration must explicitly allow access for use by another extension.');
            }

            this.log.coralInfo('Fragment', uniqueId, 'of parent', this.parent.id, 'LOADED.', fragmentLoadTimer());
            return this;
          })
          .catch((error) => {
            const message = (error && error.message) || 'Unknown error';

            this.log.coralInfo('Fragment', uniqueId, 'of parent', this.parent.id, 'failed to load because of error: ',
              message, fragmentLoadTimer(error));
            this.dispose();

            throw error;
          })
          .then((result) => {
            // Make sure to clean up the fragment and scope if navigation was cancelled or because of an error
            if (!result) {
              this.dispose();
            }
            return result;
          }));
      });
      return this.loadAndStartPromise;
    }

    /**
     * Load both the descriptor and the markup first and deal with loading errors from both resources. Then start
     * loading the function module.
     * @returns {Promise} a promise that resolves with the loaded fragment artifacts, as an array, primarily the
     * descriptor and the template. when descriptor fails to load this rejects with an error
     * @private
     */
    loadFragment() {
      // Keep a reference of the loading promise so that multiple function can wait
      // on the same promise to be resolved.
      this.loadFragmentPromise = this.loadFragmentPromise
        || Promise.all([this.loadDescriptor().catch((err) => err), this.loadTemplate()])
          .then((results) => {
            // when the descriptor fails to load, we need to consider that fragment loading has failed because there
            // is no definition! it's ok not to have JS defined!
            // TODO: it seems we could fallback to a noop descriptor and this.definition so downstream code does
            //  not fail, but that's a larger conversation
            if (results[0] instanceof Error) {
              const uniqueId = `[${this.id}: ${this.resourceName}]`;
              throw (new Error(`unable to load the descriptor for fragment ${uniqueId}`));
            }
            // create the facadeContext early, even before the facade; this requires getters
            this.getAvailableContexts();

            // Setup the component event listeners
            this.initializeEvents();

            // initialize action chains
            this.initializeActionChains();

            return results;
          })
          // make sure that the functions are loaded so that they can be used in 'vbBeforeEnter' event. Also load
          // functions after the descriptor as the latter waits for the extensions to be fully loaded.
          .then((results) => this.loadFunctionModule().then(() => results));
      return this.loadFragmentPromise;
    }

    run() {
      this.runPromise = this.getInitializePromise()
        .then(() => {
          // if the fragment has already been 'entered' do not fire a vbEnter event again. This can happen when the
          // fragment that was previously entered, is disconnected and then re-connected - where the fragment isn't
          // disposed and recreated.
          if (this.lifecycleState !== Constants.ContainerState.ENTERED
            && this.fragmentLifecycleState === Constants.FragmentState.MODULE_CONNECTED) {
            this.lifecycleState = Constants.ContainerState.ENTERED;
            return this.invokeEvent(Constants.ENTER_EVENT);
          }
          return Promise.resolve();
        })
        .then(() => {
          // TODO: record a container activated state change; unclear what this is for!
          StateMonitor.recordStateChange(StateMonitor.RuntimeState.FRAGMENT_ACTIVATED, this);

          // This event will only be fired if there are aiResponseEventListeners registered for this fragment.
          this.application.aiEventBus.fireAiPluginMountedEvent(this);
        });
      return this.runPromise;
    }

    /**
     * The name of the runtime environment function to be used to load the html
     *
     * @type {String} the template loader function name
     */
    static get templateLoaderName() {
      return 'getFragmentTemplate';
    }

    /**
     * Updates the variables with the new input params that are coming in. We take the value and overwrite the
     * variable default value. If there are live expressions setup on the variable they will no longer work since
     * the value might overwrite that.
     *
     * @param {string} name
     * @param {object} paramValue includes the 'value' property
     */
    updateParam(name, paramValue) {
      // TODO: input param needs to be 're-applied' on the current fragment variables
      this.inputParams[name] = paramValue;

      // update the variable value with the inputParam value
      if (this.scope) {
        const variable = this.scope.getVariable(name);
        if (variable) {
          this.log.info('input param updating fragment variable', variable.name, 'with value', paramValue.value);

          // make the prototype value available to the helper so it can be used for picking arrays
          this.scope.variableNamespaces[Constants.VariableNamespace.VARIABLES][name] = paramValue.value;
        }
      } else {
        this.log.finer('unable to update fragment variable with value', paramValue.value, 'as the fragment scope',
          'has not been created yet.');
      }
    }

    /**
     * returns an error message when the options are incorrect. navigateAction within fragment must include the
     * application property in the params
     * @param options
     */
    validateNavigation(options) {
      if (options.operation !== Constants.NavigateOperation.APP_UI) {
        throw new Error('The \'application\' property is required but not set!');
      }

      // Always enforce the page to be navigable when navigating from a fragment. It could be
      // in the same App UI as long as the page is navigable.
      this.application.appUiInfos.validateNavigation(options);
    }
  }

  return Fragment;
});

